import { Icon } from "@iconify/react/dist/iconify.js";
import {
  Autocomplete,
  GoogleMap,
  MarkerF,
  PolygonF,
  PolylineF,
  useJsApiLoader,
} from "@react-google-maps/api";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMedia } from "use-media";
import useGetData from "../../api/useGetData";
import dropoffIcon from "../../assets/dropoff.png";
import pickupIcon from "../../assets/pickup.png";
import { setLoaderState } from "../../redux/slices/loaderSlice";
import { isPointInPolygon } from "../../utils/mapUtils";
import Button from "../Button/Button";
import "./MapModal.css";

const libraries = ["places", "geometry"];
const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

/**
 * A component that shows a modal for the user to select the location/desitnation depending on the service that is selected by them.
 * It provides selecting the location by clicking on the map, or by searching for a place in a searchbox
 * The selected place is checked whether it lies inside the boundaries of the zones provided from the backend, and are also verified through the backend whether the distance to them is long enough or are on available roads.
 *
 * @param {Object} props
 * @param {boolean} props.isModalOpen - A flag indicates whether the modal is open or not
 * @param {"airportRide" | "cityTrip" | "rideByHour"} props.rideName - The type of service currently selected by the user
 * @param {Object} props.formValues - The current form values provided from the users
 * @param {{map: number[][]}[]} props.zoneCoords - The zones in the current city which the user is allowed to selected from
 * @param {(data: any)=>void} props.confirm - A callback function that gets called when the user successfully submits the locations
 * @returns {JSX.Element}
 */

export default function MapModal({
  isModalOpen,
  closeModal,
  rideName,
  formValues,
  zoneCoords,
  confirm,
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation("global");
  const dispatch = useDispatch();
  const pickupBox = useRef(null);
  const dropoffBox = useRef(null);
  const navigate = useNavigate();
  const isSmallScreen = useMedia("(max-width: 768px)");
  const mapRef = useRef(null);
  const [error, setError] = useState("");
  const [location, setLocation] = useState(formValues.location);
  const [destination, setDestination] = useState(formValues.destination);
  const [locationCoords, setLocationCoords] = useState(
    formValues.locationCoords
  );
  const [destinationCoords, setDestinationCoords] = useState(
    formValues.destinationCoords
  );
  const {
    getData: checkBookingZone,
    errors: checkBookingZoneErrors,
    loading: isCheckBookingZone,
  } = useGetData({
    route: "api/method/airport_transport.api.bookings.check_booking_zone",
    notLoadData: true,
  });

  const containerStyle = {
    width: "100%",
    height: `${isSmallScreen ? "50vh" : "100vh"}`,
  };
  const center =
    locationCoords ||
    destinationCoords ||
    (formValues.arrivalCity === t("hero.dammam_text")
      ? {
          lat: 26.3927,
          lng: 49.9777,
        }
      : {
          lng: 46.6753,
          lat: 24.7136,
        });
  const convertedCoords = zoneCoords.map((coord) =>
    coord.map.map((coo) => ({
      lat: coo[0],
      lng: coo[1],
    }))
  );

  useEffect(() => {
    const handlePopState = () => {
      navigate("/");
    };
    window.addEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const setPickOrDrop = async () => {
      if (rideName === "airportRide") {
        dispatch(setLoaderState(true));
        let point;
        const encodedAddress = encodeURIComponent(
          `${formValues.airportName} ${formValues.terminalNumber}`
        ); // Encode the address
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`;
        try {
          const response = await axios.get(url);
          const location = response.data.results[0].geometry.location;
          const latitude = location.lat;
          const longitude = location.lng;
          point = { lat: latitude, lng: longitude };
        } catch (error) {
          console.error("Error fetching location:", error.message);
        }
        if (formValues.rideType === t("hero.pickup_value_text")) {
          setLocation(
            `${formValues.airportName} ${formValues.terminalNumber} ${formValues.arrivalCity} Saudi Arabia`
          );
          setLocationCoords(point);
        } else if (formValues.rideType === t("hero.dropoff_value_text")) {
          setDestination(
            `${formValues.airportName} ${formValues.terminalNumber} ${formValues.arrivalCity} Saudi Arabia`
          );
          setDestinationCoords(point);
        }
        dispatch(setLoaderState(false));
      }
    };
    setPickOrDrop();
  }, [
    dispatch,
    formValues.airportName,
    formValues.arrivalCity,
    formValues.rideType,
    formValues.terminalNumber,
    rideName,
    t,
  ]);

  const onMapClick = async (event, index) => {
    const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    if (!isPointInPolygon(point, convertedCoords, index)) {
      dispatch(setLoaderState(false));
      setError(t("errors.allowed_areas"));
      return;
    }
    setError("");
    dispatch(setLoaderState(true));
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${point.lat},${point.lng}&key=${apiKey}`;
    try {
      const response = await axios.get(url);
      const mapLocation = response.data.results[0].formatted_address;
      if (rideName === "airportRide" || rideName === "rideByHour") {
        if (
          formValues.rideType === t("hero.dropoff_value_text") ||
          rideName === "rideByHour"
        ) {
          setLocation(`${mapLocation} Saudi Arabia`);
          setLocationCoords(point);
        } else {
          setDestination(`${mapLocation} Saudi Arabia`);
          setDestinationCoords(point);
        }
      } else if (rideName === "cityTrip") {
        if (location) {
          setDestination(`${mapLocation} Saudi Arabia`);
          setDestinationCoords(point);
        } else {
          setLocation(`${mapLocation} Saudi Arabia`);
          setLocationCoords(point);
        }
      }
    } catch (error) {
      console.error("Error fetching location:", error.message);
    }
    dispatch(setLoaderState(false));
  };

  async function submitDestination() {
    const checkBothLocAndDest =
      rideName === "airportRide" || rideName === "cityTrip";
    const hasLocation = location && locationCoords;
    const hasDestination = destination && destinationCoords;
    if (checkBothLocAndDest && hasLocation && hasDestination) {
      const response = await checkBookingZone({
        params: {
          coordinates: `${destinationCoords?.lat},${destinationCoords?.lng}`,
          service: rideName === "airportRide" ? "Airport Trip" : "City Trip",
          language,
          pick_up: `${locationCoords?.lat},${locationCoords?.lng}`,
          city: formValues.arrivalCity,
        },
      });
      if (response?.status === 200) {
        confirm({ location, destination, locationCoords, destinationCoords });
        closeModal();
      }
    } else if (checkBothLocAndDest) {
      setError(t("hero.errors.map_set_locations"));
    } else if (hasLocation) {
      confirm({ location, locationCoords });
      closeModal();
    } else {
      setError(t("errors.pickup_error"));
    }
  }

  useEffect(() => {
    if (!isCheckBookingZone && checkBookingZoneErrors) {
      const msg = checkBookingZoneErrors;
      if (
        msg.toLowerCase() ===
        "the booking distance is very short, please modify the reservation locations"
      ) {
        setError(t("hero.errors.short_distance"));
      } else if (
        msg.toLowerCase() ===
        "no drivable route found between location and destination, please select another location or destination"
      ) {
        setError(t("hero.errors.no_route"));
      } else {
        setError(msg);
      }
    }
  }, [checkBookingZoneErrors, isCheckBookingZone]);

  const onLoad = (ref) => {
    mapRef.current = ref;
  };

  const onPickupSearchBoxLoad = (ref) => {
    pickupBox.current = ref;
  };

  const onPlacesChangedPickup = () => {
    try {
      setError("");
      if (!pickupBox.current) {
        return;
      }
      const place = pickupBox.current.getPlace();
      const formattedAddress = place.formatted_address || "";
      setLocation(formattedAddress);
      let placePoint = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      if (isPointInPolygon(placePoint, convertedCoords, null)) {
        setLocationCoords(placePoint);
      } else {
        setError(t("errors.allowed_areas"));
      }
    } catch (err) {
      setError("Couldn't set the selected place");
      console.log(err);
    }
  };

  const onDropoffSearchBoxLoad = (ref) => {
    dropoffBox.current = ref;
  };

  const onPlacesChangedDropoff = () => {
    try {
      setError("");
      if (!dropoffBox.current) {
        return;
      }
      const place = dropoffBox.current.getPlace();
      const formattedAddress = place.name || "";
      setDestination(formattedAddress);
      let placePoint = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      if (isPointInPolygon(placePoint, convertedCoords, null)) {
        setDestinationCoords(placePoint);
      } else {
        setError(t("errors.allowed_areas"));
      }
    } catch (err) {
      setError("Couldn't set the selected place");
      console.log(err);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isModalOpen]);

  const mapOptions = {
    streetViewControl: false, // Hides the Street View control
    zoomControl: true,
    fullscreenControl: false,
    gestureHandling: "greedy",
  };

  useEffect(() => {
    if (rideName === "airportRide") {
      if (locationCoords === null) {
        setLocation("");
      }
      if (destinationCoords === null) {
        setDestination("");
      }
    }
  }, [destinationCoords, locationCoords, rideName]);

  const handleCurrentLocation = () => {
    setError("");
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          if (isPointInPolygon(currentLocation, convertedCoords, null)) {
            if (rideName === "airportRide") {
              if (formValues.rideType === t("hero.pickup_value_text")) {
                setDestinationCoords(currentLocation);
              } else {
                setLocationCoords(currentLocation);
              }
            } else if (rideName === "cityTrip") {
              if (!locationCoords) {
                setLocationCoords(currentLocation);
              } else {
                setDestinationCoords(currentLocation);
              }
            } else if (rideName === "rideByHour") {
              setLocationCoords(currentLocation);
            }
            mapRef.current.panTo(currentLocation);
          } else {
            setError(t("errors.allowed_areas"));
          }
        },
        () => {
          alert(
            "Geolocation is not supported by this browser or permission denied."
          );
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const isPickupReadOnly = () => {
    if (rideName === "airportRide") {
      return formValues.rideType === t("hero.pickup_value_text");
    }
    return !(rideName === "cityTrip" || rideName === "rideByHour");
  };

  const isDropoffReadOnly = () => {
    if (rideName === "airportRide") {
      return formValues.rideType === t("hero.dropoff_value_text");
    }
    return rideName !== "cityTrip";
  };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries,
    region: "sa",
  });
  if (!isLoaded) {
    return (
      <div
        id="extralarge-modal"
        tabIndex="-1"
        className="fixed top-0 left-0 right-0 z-50 flex justify-center items-center w-full h-full max-h-full bg-black bg-opacity-50 overflow-hidden"
      />
    );
  }
  return (
    <div
      id="extralarge-modal"
      tabIndex="-1"
      className="fixed top-0 left-0 right-0 z-50 flex justify-center items-center w-full h-full max-h-full bg-black bg-opacity-50 overflow-hidden"
    >
      <div className="relative w-full max-w-full max-h-full h-full overflow-hidden">
        <div className="relative bg-background_grey rounded-lg shadow dark:bg-gray-700 overflow-hidden">
          <div className="grid lg:grid-cols-[3fr_9fr] md:grid-cols-[4fr_8fr] sm:grid-cols-1 p-0 h-full">
            <div className="container mx-auto p-0 sm:p-0 md:p-4 order-2 sm:order-2 md:order-1">
              <div className="bg-background_white md:rounded-lg md:border-2 md:border-border_color p-3">
                <h4 className="text-lg text-text_black font-medium text-gray-900 dark:text-white">
                  {t("hero.select_map")}
                </h4>

                <div style={{ position: "relative" }}>
                  {isPickupReadOnly() ? (
                    <>
                      <input
                        type="text"
                        placeholder={t("hero.pickup_text_placeholder")}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #E9B838",
                          paddingRight: "35px",
                          outline: "none",
                          borderRadius: "7px",
                          marginTop: "10px",
                        }}
                        readOnly
                        value={location}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "60%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <img
                          src={pickupIcon}
                          alt="Pickup"
                          height={20}
                          width={20}
                        />
                      </span>
                    </>
                  ) : (
                    <>
                      <Autocomplete
                        onLoad={onPickupSearchBoxLoad}
                        onPlaceChanged={onPlacesChangedPickup}
                        options={{
                          componentRestrictions: { country: "sa" },
                          fields: [
                            "name",
                            "formatted_address",
                            "geometry.location",
                          ],
                        }}
                      >
                        <input
                          type="text"
                          placeholder={t("hero.pickup_text_placeholder")}
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #E9B838",
                            paddingRight: "35px",
                            outline: "none",
                            borderRadius: "7px",
                            marginTop: "10px",
                          }}
                          value={location}
                          onKeyUp={(e) => {
                            if (e.key === "Enter") {
                              if (
                                location &&
                                locationCoords &&
                                (rideName === "rideByHour" ||
                                  (destination && destinationCoords))
                              ) {
                                submitDestination();
                              }
                            }
                          }}
                          onChange={(e) => {
                            setLocation(e.target.value);
                            setLocationCoords(null);
                          }}
                        />
                      </Autocomplete>
                      <span
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "60%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <img
                          src={pickupIcon}
                          alt="Pickup"
                          height={20}
                          width={20}
                        />
                      </span>
                    </>
                  )}
                </div>

                <div style={{ position: "relative" }}>
                  {(rideName === "airportRide" || rideName === "cityTrip") &&
                    (isDropoffReadOnly() ? (
                      <>
                        <input
                          type="text"
                          placeholder={t("hero.dropoff_text_placeholder")}
                          readOnly
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #E9B838",
                            paddingRight: "35px",
                            outline: "none",
                            borderRadius: "7px",
                            marginTop: "10px",
                          }}
                          value={destination}
                        />
                        <span
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "60%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                          }}
                        >
                          <img
                            src={dropoffIcon}
                            alt="Pickup"
                            height={20}
                            width={20}
                          />
                        </span>
                      </>
                    ) : (
                      <>
                        <Autocomplete
                          onLoad={onDropoffSearchBoxLoad}
                          onPlaceChanged={onPlacesChangedDropoff}
                          options={{
                            componentRestrictions: { country: "sa" },
                            // You can set bounds here if necessary
                            fields: [
                              "name",
                              "formatted_address",
                              "geometry.location",
                            ],
                          }}
                        >
                          <input
                            type="text"
                            placeholder={t("hero.dropoff_text_placeholder")}
                            style={{
                              width: "100%",
                              padding: "10px",
                              border: "1px solid #E9B838",
                              paddingRight: "35px",
                              outline: "none",
                              borderRadius: "7px",
                              marginTop: "10px",
                            }}
                            value={destination}
                            onKeyUp={(e) => {
                              if (e.key === "Enter") {
                                if (
                                  location &&
                                  destination &&
                                  locationCoords &&
                                  destinationCoords
                                ) {
                                  submitDestination();
                                }
                              }
                            }}
                            onChange={(e) => {
                              setDestination(e.target.value);
                              setDestinationCoords(null);
                            }}
                          />
                        </Autocomplete>
                        <span
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "60%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                          }}
                        >
                          <img
                            src={dropoffIcon}
                            alt="Pickup"
                            height={20}
                            width={20}
                          />
                        </span>
                      </>
                    ))}
                </div>

                <Button
                  className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium text-sm px-5 py-2.5 mt-3"
                  label={t("confirm_text")}
                  onClick={submitDestination}
                />
                <Button
                  className="bg-background_white w-full text-text_black hover:bg-gray-100 font-medium text-sm px-5 py-2.5 mt-3"
                  label={t("previous_text")}
                  onClick={closeModal}
                />
              </div>
              {error && <div className="p-4 text-text_warning">{error}</div>}
            </div>
            <div className="container mx-auto p-0 sm:p-0 md:p-4 order-1 sm:order-1 md:order-2">
              <div
                className={`md:border-2 md:rounded-lg border-0`}
                style={{ borderColor: zoneCoords[0]?.color }}
              >
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={center}
                  zoom={10}
                  onLoad={onLoad}
                  onClick={(event) => onMapClick(event, null)}
                  options={mapOptions}
                >
                  {/* Your existing map components */}
                  {convertedCoords.map((convertedCod, index) => (
                    <PolygonF
                      paths={convertedCod}
                      options={{
                        fillColor: "#4463F0",
                        fillOpacity: 0.3,
                        strokeColor: "#355E3B",
                        strokeOpacity: 1,
                        strokeWeight: 1,
                      }}
                      onClick={(event) => onMapClick(event, index)}
                      key={index}
                    />
                  ))}

                  {locationCoords && (
                    <MarkerF
                      position={locationCoords}
                        label={{
                        text: t("hero.pickup_point"), 
                        color: "#000000",               
                        fontSize: "11px",                
                      }}
                      icon={{ url: "./assets/map/pickup.png",
                        labelOrigin: new window.google.maps.Point(15, 35), // Adjust as needed (x, y)
                       }}
                      onClick={() => {
                        if (
                          rideName === "airportRide" &&
                          formValues.rideType === t("hero.pickup_value_text")
                        ) {
                          return;
                        }
                        setLocation("");
                        setLocationCoords(null);
                      }}
                    />
                  )}
                  {destinationCoords && (
                    <MarkerF
                      position={destinationCoords}
                       label={{
                        text: t("hero.dropoff_point"),  // The label text
                        color: "#000000",                // Text color
                        fontSize: "11px",                // Font size
                      }}
                      icon={{ url: "./assets/map/dropoff.png",
                        scaledSize: new window.google.maps.Size(30, 30), // Set the icon size here
                        labelOrigin: new window.google.maps.Point(15, 35), // Adjust as needed (x, y)
                       }}
                      onClick={() => {
                        if (
                          rideName === "airportRide" &&
                          formValues.rideType === t("hero.dropoff_value_text")
                        ) {
                          return;
                        }
                        setDestination("");
                        setDestinationCoords(null);
                      }}
                    />
                  )}

                  {locationCoords !== null && destinationCoords !== null ? (
                    <PolylineF
                      path={[locationCoords, destinationCoords]}
                      options={{
                        strokeColor: "#E9B838",
                        strokeOpacity: 1,
                        strokeWeight: 2,
                      }}
                    />
                  ) : null}

                  <button
                    onClick={handleCurrentLocation}
                    type="button"
                    className="absolute top-3 right-2 z-10 bg-background_white text-text_black p-2 rounded-lg"
                  >
                    <Icon
                      icon="basil:current-location-outline"
                      width="24"
                      height="24"
                    />
                  </button>
                </GoogleMap>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
